/**
 * MosaicActionPanel.js — v6.32.162
 *
 * Renders the composite + 4-domain Action Panel returned from
 * POST /api/mosaic/action-panel. Each domain card shows the active
 * Can-Do / Ready-For / Scaffolds statements at the student's level.
 */
import React from 'react';
import MosaicBucketBadge from './MosaicBucketBadge';
import { Sparkles, Compass, LifeBuoy } from 'lucide-react';

const DOMAIN_ORDER = ['listening', 'speaking', 'reading', 'writing'];

const MosaicActionPanel = ({ panel }) => {
  if (!panel) return null;
  const composite = panel.composite ?? 0;
  const meta = panel.composite_meta || {};

  const domainMap = Object.fromEntries((panel.domains || []).map((d) => [d.code, d]));

  return (
    <div data-testid="mosaic-action-panel" className="border-2 border-gray-900 bg-white">
      {/* Composite header */}
      <div className="flex items-center justify-between px-5 py-4 bg-gray-900 text-white">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-gray-300">
            MOSAIC Composite
          </div>
          <div className="flex items-baseline gap-3 mt-1">
            <span className="text-3xl font-bold tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {Number(composite).toFixed(2)}
            </span>
            <MosaicBucketBadge level={panel.composite_level} label={meta.label} />
          </div>
        </div>
        <div className="text-right max-w-xs hidden md:block">
          <div className="text-xs text-gray-400 mb-0.5">Current band</div>
          <div className="text-sm leading-snug">{meta.summary}</div>
        </div>
      </div>

      {/* Domain grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-300">
        {DOMAIN_ORDER.map((code) => {
          const d = domainMap[code];
          if (!d) return null;
          return (
            <div
              key={code}
              data-testid={`mosaic-domain-${code}`}
              className="p-5 border-b border-gray-300 md:[&:nth-child(-n+2)]:border-b md:[&:nth-child(n+3)]:border-b-0"
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-gray-500">
                    Domain · {Math.round(d.weight * 100)}% weight
                  </div>
                  <div className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {d.label}
                  </div>
                </div>
                <MosaicBucketBadge level={d.level_int} label={d.level_meta?.label} compact />
              </div>

              <ul className="space-y-3">
                <li>
                  <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-emerald-700 mb-1">
                    <Sparkles className="w-3.5 h-3.5" /> Can do now
                  </div>
                  <ul className="ml-1 text-sm text-gray-800 space-y-1">
                    {d.can_do.map((s, i) => <li key={i} className="leading-snug">• {s}</li>)}
                  </ul>
                </li>
                <li>
                  <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-blue-700 mb-1">
                    <Compass className="w-3.5 h-3.5" /> Ready for next
                  </div>
                  <ul className="ml-1 text-sm text-gray-800 space-y-1">
                    {d.ready_for.map((s, i) => <li key={i} className="leading-snug">• {s}</li>)}
                  </ul>
                </li>
                <li>
                  <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-amber-700 mb-1">
                    <LifeBuoy className="w-3.5 h-3.5" /> Scaffolds to use
                  </div>
                  <ul className="ml-1 text-sm text-gray-800 space-y-1">
                    {d.scaffolds.map((s, i) => <li key={i} className="leading-snug">• {s}</li>)}
                  </ul>
                </li>
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MosaicActionPanel;
